import {
  aggregatedGoodCategoriesByGoodFamilies,
  GenericCombobox,
  GoodsFamily,
  InputGroup,
  NON_EU_COUNTRIES,
  NumericInputGroup,
  PageSection
} from '@/common'
import { parsePeriodId } from '@artem-cbam-declarants/shared'
import { ImportedGoodFormData } from './ImportedGoodForm.schema'
import useI18n from '@/shared/hooks/usei18n'
import { CnCodeSelector } from '@/shared/components/CnCodeSelector'
import { useGoodsCodes } from '@/shared/hooks/goodsCodes'
import { orderBy } from 'es-toolkit'
import { useMemo } from 'react'
import { DeclarantDatePickerSingle } from '@/shared/components'

export default function ImportedGoodDetailsForm({
  data,
  onChange,
  goodsFamily,
  isUsingDefaultEmissions,
  hasSelectedInstallation,
  periodId
}: {
  data: ImportedGoodFormData
  goodsFamily?: GoodsFamily
  onChange: (data: ImportedGoodFormData) => void
  isUsingDefaultEmissions: boolean
  hasSelectedInstallation: boolean
  periodId: string
}) {
  const { t } = useI18n()
  const aggregatedGoodCategories = goodsFamily
    ? aggregatedGoodCategoriesByGoodFamilies[goodsFamily]
    : []
  const aggregatedGoodCategoriesValues = aggregatedGoodCategories.map(
    aggregatedGoodCategory => aggregatedGoodCategory.value
  )

  const { data: goodsCodes } = useGoodsCodes({
    aggregatedGoodsCategories:
      aggregatedGoodCategoriesValues.length > 0 ? aggregatedGoodCategoriesValues : undefined
  })

  const countryOptions = useMemo(() => {
    return orderBy(
      NON_EU_COUNTRIES.map(country => ({
        value: country.value,
        label: t(`common:countriesByIsoAlpha2:${country.value}`)
      })),
      [option => option.label.toLowerCase().normalize('NFKD')],
      ['asc']
    )
  }, [t])

  const { description, cnCode, netMassInTonnes, country, customId, importDate } = data

  const { startMonth, endMonth, disabledDates } = useMemo(() => {
    const { startDate, endDate } = parsePeriodId(periodId)

    return {
      startMonth: startDate,
      endMonth: endDate,
      disabledDates: [{ before: startDate }, { after: endDate }]
    }
  }, [periodId])

  const onCnCodeUpdate = (value: string) => {
    const goodsCode = goodsCodes?.find(goodsCode => goodsCode.goodsCode === value)
    if (goodsCode && isUsingDefaultEmissions) {
      onChange({
        ...data,
        cnCode: value
      })
    } else {
      onChange({ ...data, cnCode: value })
    }
  }

  return (
    <PageSection
      title={t('cbamReporting:cbamReportDetails.importedGoodCreateDialog.sections.goodDetails')}
    >
      <div className="grid sm:grid-cols-6 gap-4">
        <div className="col-span-3">
          <CnCodeSelector
            disabled={!isUsingDefaultEmissions}
            label={t('cbamReporting:cbamReportDetails.importedGoodCreateDialog.fields.cnCode')}
            cnCode={cnCode}
            required
            aggregatedGoodsCategories={aggregatedGoodCategoriesValues}
            onCnCodeUpdate={onCnCodeUpdate}
          />
        </div>
        <div className="col-span-2">
          <GenericCombobox
            required
            label={t('common:inputFields.country.label')}
            items={countryOptions}
            value={
              country
                ? { value: country, label: t(`common:countriesByIsoAlpha2:${country}`) }
                : null
            }
            onChange={evt => onChange({ ...data, country: evt?.value || '' })}
            disabled={hasSelectedInstallation}
          />
        </div>
        <NumericInputGroup
          label={t('cbamReporting:cbamReportDetails.importedGoodCreateDialog.fields.quantity')}
          value={netMassInTonnes.toString()}
          required
          suffix={'t'}
          numericFormat={{
            allowNegative: false,
            decimalScale: 6
          }}
          onChange={evt => onChange({ ...data, netMassInTonnes: Number(evt.target.value) })}
        />
      </div>
      <div className="grid sm:grid-cols-5 gap-4">
        <div className="col-span-2">
          <InputGroup
            disabled={!isUsingDefaultEmissions}
            label={t(
              'cbamReporting:cbamReportDetails.importedGoodCreateDialog.fields.goodsDescription'
            )}
            value={description || ''}
            onChange={value => onChange({ ...data, description: value.target.value })}
          />
        </div>
        <div className="col-span-2">
          <InputGroup
            label={t('cbamReporting:cbamReportDetails.importedGoodCreateDialog.fields.customId')}
            value={customId || ''}
            onChange={value => onChange({ ...data, customId: value.target.value })}
          />
        </div>
        <DeclarantDatePickerSingle
          id="importDate"
          label={t('cbamReporting:cbamReportDetails.importedGoodCreateDialog.fields.importDate')}
          required
          date={importDate || ''}
          startMonth={startMonth}
          endMonth={endMonth}
          disabled={disabledDates}
          onChange={value => onChange({ ...data, importDate: value })}
        />
      </div>
    </PageSection>
  )
}
